// Lote G (M51), PO 21: pestaña "Matriz por puesto" de la pantalla de EPP. Dos partes:
//  1. Matriz de EPP por puesto: por cada puesto, el EPP del catálogo que requiere, la especificación o norma, la
//     frecuencia de reposición y una observación. Parte de lo que ya registran las evaluaciones de riesgos del puesto
//     (sugerencias que el usuario confirma). No copia datos del puesto: el nombre se lee de Puestos de Trabajo.
//  2. Procedimiento de adquisición de EPP, igual que los procedimientos de GT 37: el borrador (texto base editable) y
//     cada versión aprobada (texto y matriz congelados con SHA-256), firmada por el responsable de SSO en pantalla o
//     con el formato firmado a mano y su escaneo. La aprobada no se edita ni se borra: para cambiar algo se edita el
//     borrador y se aprueba una versión nueva. Reutiliza SignaturePad (permisos, plan, inspecciones y programas).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad, { type SignaturePadHandle } from '../permits/SignaturePad';
import { ModalConfirmar } from '../../pages/pe/ComponentesPE';
import '../../pages/pe/PlanEmergenciasV2.css';
import {
  borradorSinAprobar, coberturaMatriz, faltantesParaAprobar, filaMatrizNueva, NOTA_PLAZOS_PO21, resultadoPO21, sugerenciasPuesto,
  TEXTO_BASE_PROCEDIMIENTO, textoReposicion, ultimaAprobada, venceProcedimiento, type ContenidoProcedimiento, type EppMin, type FilaMatriz,
} from '../../lib/epp/epp-po21';
import {
  agregarFilas, aprobarProcedimiento, borrarFila, cargarMatrizEpp, crearProcedimiento, editarFila, guardarTexto, type DatosMatrizEpp,
} from '../../lib/epp/epp-po21-datos';
import { ACEPTA_ARCHIVO } from '../../lib/informes-puesto/ip-datos';
import { TEXTO_FIRMA_APROBACION } from '../../lib/informes-puesto/ip-tipos';
import { cedulaValida } from '../../lib/firmas/firmas-logica';
import { fechaEc } from '../../lib/fechas';
import { plural } from '../../lib/plurales';

export interface MatrizEppPuestoProps {
  companyId: string;
  empresa: string;
  puedeEditar: boolean;
  catalogo: EppMin[];
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
// approved_at es un instante: se muestra el día de Ecuador (no el de UTC) con lib/fechas.
const fecha = (iso: string | null) => fechaEc(iso);

const S = {
  caja: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 } as React.CSSProperties,
  h3: { color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 700, margin: 0 } as React.CSSProperties,
  nota: { color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: 1.45, overflowWrap: 'anywhere' } as React.CSSProperties,
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', padding: '8px 10px', fontSize: '0.86rem', minHeight: 40 } as React.CSSProperties,
  etiqueta: { color: '#94a3b8', fontSize: '0.74rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } as React.CSSProperties,
  fila: { border: '1px solid #334155', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 } as React.CSSProperties,
  botones: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' } as React.CSSProperties,
};
const boton = (tipo: 'primario' | 'secundario' | 'peligro' = 'secundario', off = false): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: 8, fontWeight: 600, fontSize: '0.84rem', minHeight: 40, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.55 : 1,
  ...(tipo === 'primario' ? { background: '#2FBF71', border: '1px solid #2FBF71', color: '#0f172a' }
    : tipo === 'peligro' ? { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171' }
      : { background: 'transparent', border: '1px solid #475569', color: '#cbd5e1' }),
});
const COLOR: Record<string, string> = { cumple: '#2FBF71', en_proceso: '#f59e0b', no_cumple: '#ef4444', pendiente: '#94a3b8' };
const ETIQUETA_ESTADO: Record<string, string> = { cumple: 'Cumple', en_proceso: 'En proceso', no_cumple: 'No cumple', pendiente: 'Pendiente' };
const Insignia = ({ estado }: { estado: string }) => (
  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.76rem', fontWeight: 700, color: COLOR[estado], border: `1px solid ${COLOR[estado]}`, whiteSpace: 'nowrap' }}>
    PO 21: {ETIQUETA_ESTADO[estado]}
  </span>
);

// ── Fila de la matriz (guardado visible por fila) ──
function FilaEditable({ fila, epp, puedeEditar, onGuardada }: { fila: FilaMatriz; epp: EppMin | undefined; puedeEditar: boolean; onGuardada: () => void }) {
  const [spec, setSpec] = useState(fila.specification ?? '');
  const [meses, setMeses] = useState(fila.replacement_months != null ? String(fila.replacement_months) : '');
  const [obs, setObs] = useState(fila.observation ?? '');
  const [estado, setEstado] = useState<'' | 'guardando' | 'guardado' | string>('');
  const cambiado = spec !== (fila.specification ?? '') || meses !== (fila.replacement_months != null ? String(fila.replacement_months) : '') || obs !== (fila.observation ?? '');
  const mesesNum = meses.trim() === '' ? null : Number(meses);
  const mesesOk = mesesNum === null || (Number.isInteger(mesesNum) && mesesNum >= 1 && mesesNum <= 120);
  const guardar = async () => {
    setEstado('guardando');
    try { await editarFila(fila.id, { specification: spec.trim() || null, replacement_months: mesesNum, observation: obs.trim() || null }); setEstado('guardado'); onGuardada(); }
    catch (e) { setEstado(msg(e)); }
  };
  // Confirmación con el modal propio de la app (no el confirm() del navegador).
  const [confirmar, setConfirmar] = useState(false);
  const [quitando, setQuitando] = useState(false);
  const cancelarQuitar = useCallback(() => setConfirmar(false), []);
  const quitar = async () => {
    setQuitando(true);
    try { await borrarFila(fila.id); setConfirmar(false); onGuardada(); } catch (e) { setConfirmar(false); setEstado(msg(e)); }
    finally { setQuitando(false); }
  };
  return (
    <div style={S.fila}>
      <div style={S.botones}>
        <strong style={{ color: '#e2e8f0', fontSize: '0.88rem', overflowWrap: 'anywhere' }}>{epp?.name ?? 'EPP borrado del catálogo'}</strong>
        {fila.origin === 'sugerencia' && <span style={{ ...S.nota, fontSize: '0.72rem' }}>(sugerido por los riesgos del puesto, confirmado)</span>}
      </div>
      {puedeEditar ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          <label style={S.etiqueta}>Especificación o norma
            <input style={S.input} value={spec} maxLength={500} placeholder="Ej.: guantes de nitrilo, talla M" onChange={e => { setSpec(e.target.value); setEstado(''); }} />
          </label>
          <label style={S.etiqueta}>Reposición (meses)
            <input style={S.input} inputMode="numeric" value={meses} placeholder={epp?.lifespan_months ? `Vida útil: ${epp.lifespan_months}` : 'Ej.: 6'}
              onChange={e => { setMeses(e.target.value.replace(/[^0-9]/g, '')); setEstado(''); }} aria-invalid={!mesesOk} />
          </label>
          <label style={S.etiqueta}>Observación
            <input style={S.input} value={obs} maxLength={500} onChange={e => { setObs(e.target.value); setEstado(''); }} />
          </label>
        </div>
      ) : (
        <p style={S.nota}>{fila.specification ?? 'Sin especificación'} · {textoReposicion(fila.replacement_months)}{fila.observation ? ` · ${fila.observation}` : ''}</p>
      )}
      {puedeEditar && (
        <div style={S.botones}>
          <button type="button" style={boton('primario', !cambiado || !mesesOk || estado === 'guardando')} disabled={!cambiado || !mesesOk || estado === 'guardando'} onClick={guardar}>
            {estado === 'guardando' ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" style={boton('peligro')} onClick={() => setConfirmar(true)}>Quitar</button>
          <ModalConfirmar abierto={confirmar} titulo="Quitar de la matriz" textoConfirmar="Quitar" ocupado={quitando}
            mensaje={`¿Quitar "${epp?.name ?? 'este EPP'}" de la matriz de este puesto? Las versiones ya aprobadas del procedimiento no cambian.`}
            onConfirmar={quitar} onCancelar={cancelarQuitar} />
          {!mesesOk && <span role="alert" style={{ ...S.nota, color: '#fca5a5' }}>La reposición va de 1 a 120 meses.</span>}
          {estado === 'guardado' && !cambiado && <span role="status" style={{ ...S.nota, color: '#2FBF71' }}>✓ Guardado</span>}
          {cambiado && estado === '' && <span style={S.nota}>Cambios sin guardar</span>}
          {estado && !['guardando', 'guardado'].includes(estado) && <span role="alert" style={{ ...S.nota, color: '#fca5a5' }}>{estado}</span>}
        </div>
      )}
    </div>
  );
}

export default function MatrizEppPuesto({ companyId, empresa, puedeEditar, catalogo }: MatrizEppPuestoProps) {
  const [datos, setDatos] = useState<DatosMatrizEpp | null | undefined>(undefined);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [agregar, setAgregar] = useState<Record<string, string>>({});
  const [texto, setTexto] = useState('');
  const [textoGuardado, setTextoGuardado] = useState(true);
  const [metodo, setMetodo] = useState<'pantalla' | 'papel'>('pantalla');
  const [firmante, setFirmante] = useState({ nombre: '', cedula: '' });
  const [conTrazo, setConTrazo] = useState(false);
  const [escaneo, setEscaneo] = useState<File | null>(null);
  const pad = useRef<SignaturePadHandle>(null);

  const cargar = useCallback(async () => {
    try { setDatos(await cargarMatrizEpp(companyId)); setError(''); } catch (e) { setError(msg(e)); setDatos(null); }
  }, [companyId]);
  useEffect(() => { if (companyId) cargar(); }, [companyId, cargar]);

  const proc = datos?.procedimiento ?? null;
  const aprobada = useMemo(() => (datos ? ultimaAprobada(datos.versiones) : null), [datos]);
  useEffect(() => { setTexto(proc?.body ?? ''); setTextoGuardado(true); }, [proc?.id, proc?.body]);

  const accion = async (fn: () => Promise<void>, ok: string) => {
    setOcupado(true); setError(''); setAviso('');
    try { await fn(); setAviso(ok); await cargar(); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  };

  if (datos === undefined) return <p style={S.nota}>Cargando la matriz…</p>;
  if (datos === null) {
    return <div style={S.caja}><p style={S.nota}>{error || 'La matriz de EPP por puesto todavía no está disponible: falta actualizar la base de datos. Avisa al soporte de StorSafe.'}</p></div>;
  }
  const hoy = new Date();
  const resultado = resultadoPO21({ ...datos, borrador: borradorSinAprobar(proc, aprobada) }, hoy);
  const cobertura = coberturaMatriz(datos.puestos, datos.trabajadores, datos.matriz, hoy);
  const eppDe = new Map(catalogo.map(e => [e.id, e]));
  const snap = (aprobada?.snapshot ?? null) as ContenidoProcedimiento | null;

  const confirmar = (puestoId: string, epps: EppMin[]) => accion(
    () => agregarFilas(epps.map(e => filaMatrizNueva(companyId, puestoId, e, { origin: 'sugerencia' }))),
    epps.length === 1 ? `"${epps[0].name}" quedó en la matriz.` : `${epps.length} EPP quedaron en la matriz.`);
  const agregarManual = (puestoId: string) => {
    const e = eppDe.get(agregar[puestoId] ?? '');
    if (!e) return;
    accion(() => agregarFilas([filaMatrizNueva(companyId, puestoId, e)]), `"${e.name}" quedó en la matriz.`).then(() => setAgregar(a => ({ ...a, [puestoId]: '' })));
  };

  // Aprobar: el texto guardado y la matriz de hoy quedan congelados en una versión nueva (no se edita ni se borra).
  const aprobar = async () => {
    if (!proc) return;
    if (metodo === 'pantalla') {
      const trazo = await pad.current?.toBlob();
      if (!trazo || !conTrazo || pad.current?.isEmpty()) { setError('Falta la firma: firma con el dedo o el mouse en el recuadro.'); return; }
      await accion(() => aprobarProcedimiento(proc, empresa, datos, catalogo, { metodo: 'pantalla', nombre: firmante.nombre, cedula: firmante.cedula, trazo }).then(() => undefined),
        'Procedimiento aprobado. La versión queda guardada y no se puede editar.');
    } else {
      if (!escaneo) { setError('Sube el escaneo del formato firmado.'); return; }
      await accion(() => aprobarProcedimiento(proc, empresa, datos, catalogo, { metodo: 'papel', escaneo }).then(() => undefined),
        'Procedimiento aprobado con el formato firmado a mano. La versión queda guardada y no se puede editar.');
    }
    pad.current?.clear(); setConTrazo(false); setEscaneo(null); setFirmante({ nombre: '', cedula: '' });
  };

  const faltan = proc ? faltantesParaAprobar({ body: texto }, datos.matriz) : [];
  const sinCambios = !!aprobada && !!snap && snap.texto === (proc?.body ?? '') && !borradorSinAprobar(proc, aprobada);
  const firmaLista = metodo === 'pantalla' ? firmante.nombre.trim().length >= 3 && cedulaValida(firmante.cedula) && conTrazo : !!escaneo;
  const puedeAprobar = !!proc && textoGuardado && faltan.length === 0 && firmaLista && !ocupado;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.caja} data-testid="estado-po21">
        <div style={S.botones}><Insignia estado={resultado.estado} /><span style={S.nota}>Procedimiento de adquisición de EPP y ropa de trabajo con la matriz por puesto</span></div>
        <p style={S.nota}>{resultado.resumen}</p>
        <p style={{ ...S.nota, fontSize: '0.72rem' }}>{NOTA_PLAZOS_PO21}</p>
      </div>
      {aviso && <p role="status" style={{ ...S.nota, color: '#2FBF71' }}>✓ {aviso}</p>}
      {error && <p role="alert" style={{ ...S.nota, color: '#fca5a5' }}>{error}</p>}

      {/* ── 1. Matriz por puesto ── */}
      <section style={S.caja} aria-labelledby="titulo-matriz">
        <h3 id="titulo-matriz" style={S.h3}>Matriz de EPP por puesto</h3>
        <p style={S.nota}>Por cada puesto, el EPP que requiere. Las sugerencias salen del EPP que ya registran las evaluaciones de riesgos del puesto: confírmalas o agrega otro EPP del catálogo.</p>
        {datos.puestos.length === 0 && <p style={S.nota}>Todavía no hay puestos. Créalos en Puestos de Trabajo y vuelve aquí para asignarles su EPP.</p>}
        {datos.puestos.length > 0 && catalogo.length === 0 && <p style={S.nota}>El catálogo de EPP está vacío. Agrega el EPP en la pestaña Catálogo y vuelve aquí.</p>}
        {cobertura.map(c => {
          const filas = datos.matriz.filter(m => m.position_id === c.puesto.id);
          const sugeridos = sugerenciasPuesto(c.puesto.id, datos.iper, catalogo, datos.matriz);
          const disponibles = catalogo.filter(e => !filas.some(f => f.epp_id === e.id));
          const falta = c.trabajadores > 0 && filas.length === 0;
          return (
            <div key={c.puesto.id} style={{ ...S.fila, borderColor: falta ? 'rgba(245,158,11,0.5)' : '#334155' }}>
              <div style={S.botones}>
                <strong style={{ color: '#e2e8f0', overflowWrap: 'anywhere' }}>{c.puesto.name}</strong>
                <span style={S.nota}>{plural(c.trabajadores, 'trabajador activo', 'trabajadores activos')} · {plural(filas.length, 'EPP', 'EPP')}</span>
                {falta && <span style={{ ...S.nota, color: '#f59e0b' }}>⚠ Falta su EPP en la matriz</span>}
              </div>
              {filas.length === 0 && sugeridos.length === 0 && <p style={S.nota}>Sin EPP todavía. Elige un EPP del catálogo y pulsa Agregar.</p>}
              {filas.map(f => <FilaEditable key={f.id} fila={f} epp={eppDe.get(f.epp_id)} puedeEditar={puedeEditar} onGuardada={cargar} />)}
              {puedeEditar && sugeridos.length > 0 && (
                <div style={{ ...S.botones, background: 'rgba(47,191,113,0.06)', borderRadius: 8, padding: 8 }}>
                  <span style={S.nota}>Sugeridos por los riesgos del puesto:</span>
                  {sugeridos.map(e => (
                    <button key={e.id} type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => confirmar(c.puesto.id, [e])}>+ {e.name}</button>
                  ))}
                  {sugeridos.length > 1 && <button type="button" style={boton('primario', ocupado)} disabled={ocupado} onClick={() => confirmar(c.puesto.id, sugeridos)}>Confirmar todos</button>}
                </div>
              )}
              {puedeEditar && disponibles.length > 0 && (
                <div style={S.botones}>
                  <select aria-label={`EPP para ${c.puesto.name}`} style={{ ...S.input, width: 'auto', flex: '1 1 180px' }} value={agregar[c.puesto.id] ?? ''}
                    onChange={e => setAgregar(a => ({ ...a, [c.puesto.id]: e.target.value }))}>
                    <option value="">Elegir EPP del catálogo…</option>
                    {disponibles.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                  <button type="button" style={boton('secundario', !agregar[c.puesto.id] || ocupado)} disabled={!agregar[c.puesto.id] || ocupado} onClick={() => agregarManual(c.puesto.id)}>Agregar</button>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* ── 2. Procedimiento ── */}
      <section style={S.caja} aria-labelledby="titulo-proc">
        <h3 id="titulo-proc" style={S.h3}>Procedimiento de adquisición de EPP</h3>
        {aprobada && (
          <div style={S.fila}>
            <p style={{ ...S.nota, color: '#e2e8f0' }}>
              Versión {aprobada.version} <strong style={{ color: '#2FBF71' }}>aprobada</strong> el {fecha(aprobada.approved_at)} · vigente hasta el {fecha(venceProcedimiento(aprobada.approved_at))}.
              {' '}{aprobada.sign_method === 'pantalla' ? `Firmada en pantalla por ${aprobada.signer_name ?? '—'} (responsable de SSO).` : 'Firmada a mano (escaneo del formato firmado).'}
              {datos.versiones.length > 1 ? ` Versiones anteriores: ${datos.versiones.length - 1}.` : ''}
            </p>
            <details>
              <summary style={{ ...S.nota, cursor: 'pointer' }}>Ver el texto y la matriz aprobados (no se pueden editar)</summary>
              <pre style={{ ...S.nota, whiteSpace: 'pre-wrap', fontFamily: 'inherit', marginTop: 8 }}>{snap?.texto ?? ''}</pre>
              <ul style={{ ...S.nota, paddingLeft: 18 }}>
                {(snap?.matriz ?? []).map((m, k) => (
                  <li key={k}>{m.puesto}: {m.epp}{m.especificacion ? ` (${m.especificacion})` : ''} · {textoReposicion(m.reposicion_meses)}{m.observacion ? ` · ${m.observacion}` : ''}</li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {!proc && (
          <>
            <p style={S.nota}>Todavía no hay procedimiento. Créalo con el texto base (selección según la matriz, compra a proveedores, verificación de lo recibido, entrega con registro firmado, reposición y baja) y ajústalo a tu empresa.</p>
            {puedeEditar && <div><button type="button" style={boton('primario', ocupado)} disabled={ocupado}
              onClick={() => accion(() => crearProcedimiento(companyId, TEXTO_BASE_PROCEDIMIENTO), 'Se creó el procedimiento en borrador. Revisa el texto y guárdalo.')}>Crear con el texto base</button></div>}
          </>
        )}

        {proc && (
          <div style={S.fila}>
            <p style={{ ...S.nota, color: '#e2e8f0' }}>
              <strong>Borrador</strong>{aprobada ? ' · para cambiar el procedimiento o la matriz anexa, edita el borrador y aprueba una versión nueva; la aprobada sigue vigente hasta entonces.' : ''}
            </p>
            {puedeEditar ? (
              <>
                <label style={S.etiqueta}>Texto del procedimiento
                  <textarea style={{ ...S.input, minHeight: 260, fontFamily: 'inherit', lineHeight: 1.45 }} value={texto} maxLength={20000}
                    onChange={e => { setTexto(e.target.value); setTextoGuardado(false); setAviso(''); }} />
                </label>
                <div style={S.botones}>
                  <button type="button" style={boton('primario', textoGuardado || ocupado)} disabled={textoGuardado || ocupado}
                    onClick={() => accion(() => guardarTexto(proc.id, texto), 'Texto guardado.')}>Guardar texto</button>
                  <span style={S.nota}>{textoGuardado ? '✓ Sin cambios por guardar' : 'Cambios sin guardar'}</span>
                </div>
                {faltan.length > 0 && <ul style={{ ...S.nota, paddingLeft: 18, color: '#fbbf24' }}>{faltan.map(f => <li key={f}>{f}</li>)}</ul>}
                {!textoGuardado && <p style={S.nota}>Guarda el texto antes de aprobarlo.</p>}
                {sinCambios && <p style={S.nota}>El texto es el de la versión {aprobada!.version}: apruébala otra vez solo si cambió la matriz anexa.</p>}

                <div style={S.fila} aria-label="Aprobar una versión">
                  <strong style={{ color: '#e2e8f0', fontSize: '0.86rem' }}>Aprobar {aprobada ? `la versión ${aprobada.version + 1}` : 'la primera versión'}</strong>
                  <p style={S.nota}>Al aprobar, el texto guardado y la matriz de hoy quedan congelados y ya no se editan. Firma el responsable de SSO.</p>
                  <div style={S.botones}>
                    <button type="button" style={boton(metodo === 'pantalla' ? 'primario' : 'secundario')} onClick={() => setMetodo('pantalla')}>Firmar en pantalla</button>
                    <button type="button" style={boton(metodo === 'papel' ? 'primario' : 'secundario')} onClick={() => setMetodo('papel')}>Firma manual</button>
                  </div>
                  {metodo === 'pantalla' ? (
                    <>
                      <label style={S.etiqueta}>Nombre del responsable de SSO<input style={S.input} value={firmante.nombre} maxLength={160}
                        onChange={e => setFirmante(x => ({ ...x, nombre: e.target.value }))} /></label>
                      <label style={S.etiqueta}>Cédula (10 dígitos)<input style={S.input} inputMode="numeric" value={firmante.cedula} maxLength={10}
                        onChange={e => setFirmante(x => ({ ...x, cedula: e.target.value.replace(/[^0-9]/g, '') }))} /></label>
                      {/* El pad trae su propio botón "Borrar firma" (avisa por onChange): no se repite aquí. */}
                      <SignaturePad ref={pad} height={170} onChange={v => setConTrazo(!v)} />
                    </>
                  ) : (
                    <label style={S.etiqueta}>Escaneo del formato firmado a mano (PDF, PNG o JPEG)
                      <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setEscaneo(e.target.files?.[0] ?? null)} style={{ color: '#cbd5e1', maxWidth: '100%' }} />
                    </label>
                  )}
                  <p style={{ ...S.nota, fontSize: '0.72rem' }}>{TEXTO_FIRMA_APROBACION}</p>
                  <div><button type="button" style={boton('primario', !puedeAprobar)} disabled={!puedeAprobar} onClick={aprobar}>
                    {metodo === 'pantalla' ? 'Aprobar y firmar' : 'Aprobar con el escaneo'}</button></div>
                </div>
              </>
            ) : (
              <pre style={{ ...S.nota, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{proc.body}</pre>
            )}
          </div>
        )}
        {!puedeEditar && <p style={S.nota}>Solo el administrador o el gerente pueden editar la matriz y el procedimiento.</p>}
      </section>
    </div>
  );
}
